import {
  DownOutlined,
  LoadingOutlined,
  QuestionOutlined
} from "@ant-design/icons"
import { App, Dropdown, Tag } from "antd"
import type { MenuItemType } from "antd/es/menu/interface"
import { type BaseOptionType } from "antd/lib/select"
import { useState } from "react"

import {
  PROJECT_STATUSES_MAP,
  PROJECT_STATUSES_OBJ
} from "../../Projects/constants/project-statuses.ts"

interface ProjectStatusSelectorProps {
  status: null | string | number
  statuses?: BaseOptionType[]
  onChange: (id: number) => Promise<void> | void
}

export function ProjectStatusSelector({
  status,
  statuses,
  onChange
}: ProjectStatusSelectorProps) {
  const { modal } = App.useApp()
  const [loading, setLoading] = useState(false)

  if (!status) return <Tag className="m-0">No status</Tag>

  const getStatusData = () => {
    if (typeof status === "number") return PROJECT_STATUSES_OBJ?.[status]
    return PROJECT_STATUSES_MAP?.[status]
  }

  const statusData = getStatusData()

  const handleChange = (value: number) => {
    modal.confirm({
      centered: true,
      icon: <QuestionOutlined />,
      title: "Confirm Status Change",
      content: "Are you sure you want to change the status of this project?",
      cancelText: "Cancel",
      okText: "Change",
      onOk: async () => {
        setLoading(true)
        try {
          await onChange(value)
        } finally {
          setLoading(false)
        }
      }
    })
  }

  const getDisabled = (s: BaseOptionType) => {
    if (typeof status === "number") return s?.value === status
    return s?.label?.toLowerCase() === status?.toLowerCase()
  }

  const items = statuses?.map((s) => {
    const isDisabled = getDisabled(s)

    return {
      ...s,
      key: s?.value,
      className: `mb-1 ${isDisabled ? "" : "hover:outline-1 hover:outline-primary/50 transition-colors duration-500"}`,
      disabled: isDisabled || loading,
      onClick: () => handleChange(s?.value),
      style: {
        ...(PROJECT_STATUSES_OBJ?.[s?.value]?.style || {}),
        opacity: isDisabled ? "50%" : "100%"
      }
    }
  }) as MenuItemType[]

  return (
    <Dropdown
      menu={{
        items,
        selectedKeys: [status?.toString()]
      }}
      trigger={["click"]}
      disabled={loading}
    >
      <Tag style={statusData?.style} className="m-0 cursor-pointer">
        {statusData?.label || "No status"}
        {loading ? (
          <LoadingOutlined className="ml-1 text-xs" />
        ) : (
          <DownOutlined className="ml-1 text-xs" />
        )}
      </Tag>
    </Dropdown>
  )
}
